import { Registry, type Identified } from './Registry';
import { gearMaterials } from './materials';
import { buildPieces } from './build';
import type { Cost } from './build';

/**
 * Üretim tarifleri.
 * Şekilli (pattern) ve şekilsiz (ingredients) tarifler desteklenir.
 * Izgara 8×8'e kadar olabilir; desen ızgara içinde herhangi bir konumda eşleşir.
 * Yeni tarif = bu dosyaya bir kayıt. Crafting sistemi değişmez.
 */

export type Station = 'hand' | 'workbench' | 'furnace' | 'grinder';

export interface RecipeDef extends Identified {
  station: Station;
  output: { item: string; count: number };
  /** Şekilli tarif satırları. Boşluk = boş hücre. */
  pattern?: string[];
  /** Desen karakteri -> item id. */
  key?: Record<string, string>;
  /** Şekilsiz tarif (sıra/konum önemsiz). */
  ingredients?: Cost[];
  /** Fırın/öğütücü işlem süresi (saniye). */
  time?: number;
  category?: string;
  desc?: string;
}

export const recipes = new Registry<RecipeDef>('recipes');

export const CRAFT_GRID = 8;

// ------------------------------------------------------------ temel tarifler
recipes.register(
  {
    id: 'stick_from_wood',
    station: 'hand',
    output: { item: 'stick', count: 4 },
    ingredients: [{ item: 'wood', count: 1 }],
    category: 'Temel',
  },
  {
    id: 'rope_from_fiber',
    station: 'hand',
    output: { item: 'rope', count: 1 },
    ingredients: [{ item: 'fiber', count: 3 }],
    category: 'Temel',
  },
  {
    id: 'bow',
    station: 'workbench',
    output: { item: 'bow', count: 1 },
    // Tasarım gereği tam olarak: 4 Çubuk + 1 İp
    ingredients: [
      { item: 'stick', count: 4 },
      { item: 'rope', count: 1 },
    ],
    category: 'Silah',
    desc: '4 Çubuk + 1 İp',
  },
  {
    id: 'arrow',
    station: 'workbench',
    output: { item: 'arrow', count: 8 },
    ingredients: [
      { item: 'stick', count: 2 },
      { item: 'stone', count: 1 },
      { item: 'feather', count: 1 },
    ],
    category: 'Silah',
  },
  {
    id: 'fishing_rod',
    station: 'workbench',
    output: { item: 'fishing_rod', count: 1 },
    ingredients: [
      { item: 'stick', count: 3 },
      { item: 'rope', count: 2 },
    ],
    category: 'Balıkçılık',
  },
  {
    id: 'fishing_rod_pro',
    station: 'workbench',
    output: { item: 'fishing_rod_pro', count: 1 },
    ingredients: [
      { item: 'fishing_rod', count: 1 },
      { item: 'silver', count: 3 },
      { item: 'rope', count: 2 },
    ],
    category: 'Balıkçılık',
  },
  {
    id: 'bread',
    station: 'workbench',
    output: { item: 'bread', count: 1 },
    ingredients: [{ item: 'wheat', count: 3 }],
    category: 'Yemek',
  },
  {
    id: 'soup',
    station: 'workbench',
    output: { item: 'soup', count: 1 },
    ingredients: [
      { item: 'cooked_meat', count: 1 },
      { item: 'wheat', count: 1 },
      { item: 'berry', count: 2 },
    ],
    category: 'Yemek',
  },
);

// ------------------------------------------------------------ kum → cam zinciri
recipes.register(
  {
    id: 'grind_sand',
    station: 'grinder',
    output: { item: 'sand_fine', count: 1 },
    ingredients: [{ item: 'sand', count: 2 }],
    time: 3,
    category: 'İşleme',
    desc: 'Sahil kumunu öğütür.',
  },
  {
    id: 'smelt_glass',
    station: 'furnace',
    output: { item: 'glass', count: 1 },
    ingredients: [{ item: 'sand_fine', count: 1 }],
    time: 5,
    category: 'İşleme',
    desc: 'İnce kumu eritip cam yapar.',
  },
  {
    id: 'cook_meat',
    station: 'furnace',
    output: { item: 'cooked_meat', count: 1 },
    ingredients: [{ item: 'raw_meat', count: 1 }],
    time: 6,
    category: 'Yemek',
  },
);

// Balıkları pişirme: her balık türü için tek tarif yerine genel bir dönüşüm
// (Fishing sistemi balığı 'fish' türü olarak işaretler, fırın kabul eder).
recipes.register({
  id: 'cook_fish',
  station: 'furnace',
  output: { item: 'cooked_fish', count: 1 },
  ingredients: [{ item: 'fish_minnow', count: 1 }],
  time: 5,
  category: 'Yemek',
  desc: 'Küçük balığı pişirir.',
});

// ------------------------------------------------------------ iksirler
recipes.register(
  {
    id: 'potion_speed',
    station: 'workbench',
    output: { item: 'potion_speed', count: 1 },
    ingredients: [
      { item: 'glow_mushroom', count: 2 },
      { item: 'glass', count: 1 },
      { item: 'berry', count: 2 },
    ],
    category: 'İksir',
  },
  {
    id: 'potion_strength',
    station: 'workbench',
    output: { item: 'potion_strength', count: 1 },
    ingredients: [
      { item: 'glow_mushroom', count: 2 },
      { item: 'glass', count: 1 },
      { item: 'raw_meat', count: 1 },
      { item: 'redstone', count: 1 },
    ],
    category: 'İksir',
  },
  {
    id: 'potion_antivenom',
    station: 'workbench',
    output: { item: 'potion_antivenom', count: 1 },
    ingredients: [
      { item: 'venom_sac', count: 1 },
      { item: 'glass', count: 1 },
      { item: 'glow_mushroom', count: 1 },
    ],
    category: 'İksir',
  },
  {
    id: 'potion_fishing',
    station: 'workbench',
    output: { item: 'potion_fishing', count: 1 },
    ingredients: [
      { item: 'fish_minnow', count: 2 },
      { item: 'glass', count: 1 },
      { item: 'kogite', count: 1 },
    ],
    category: 'İksir',
  },
  {
    id: 'potion_mining',
    station: 'workbench',
    output: { item: 'potion_mining', count: 1 },
    ingredients: [
      { item: 'coal', count: 3 },
      { item: 'glass', count: 1 },
      { item: 'redstone', count: 1 },
    ],
    category: 'İksir',
  },
  {
    id: 'potion_nightvision',
    station: 'workbench',
    output: { item: 'potion_nightvision', count: 1 },
    ingredients: [
      { item: 'glow_mushroom', count: 3 },
      { item: 'glass', count: 1 },
      { item: 'gold', count: 1 },
    ],
    category: 'İksir',
  },
  {
    id: 'potion_satiety',
    station: 'workbench',
    output: { item: 'potion_satiety', count: 1 },
    ingredients: [
      { item: 'bread', count: 1 },
      { item: 'glass', count: 1 },
      { item: 'wheat', count: 2 },
    ],
    category: 'İksir',
  },
);

// ------------------------------------------------------------ türetilmiş ekipman tarifleri
/** Araç/zırh desenleri: X = malzeme, I = çubuk. */
const GEAR_PATTERNS: Record<string, string[]> = {
  sword: ['X', 'X', 'I'],
  pickaxe: ['XXX', ' I ', ' I '],
  axe: ['XX', 'XI', ' I'],
  shovel: ['X', 'I', 'I'],
  hoe: ['XX', ' I', ' I'],
  hammer: ['XXX', 'XIX', ' I '],
  head: ['XXX', 'X X'],
  chest: ['X X', 'XXX', 'XXX'],
  legs: ['XXX', 'X X', 'X X'],
  feet: ['X X', 'X X'],
};

for (const m of gearMaterials()) {
  for (const [suffix, pattern] of Object.entries(GEAR_PATTERNS)) {
    recipes.register({
      id: `${m.id}_${suffix}`,
      station: m.id === 'wood' ? 'hand' : 'workbench',
      output: { item: `${m.id}_${suffix}`, count: 1 },
      pattern,
      key: { X: m.id, I: 'stick' },
      category: suffix === 'head' || suffix === 'chest' || suffix === 'legs' || suffix === 'feet' ? 'Zırh' : 'Araç',
    });
  }
}

// ------------------------------------------------------------ GÜÇ ZIRHI birleştirme
// Elmas + Altın + Redstone parçası -> Güç Zırhı parçası
for (const slot of ['head', 'chest', 'legs', 'feet'] as const) {
  recipes.register({
    id: `power_${slot}`,
    station: 'workbench',
    output: { item: `power_${slot}`, count: 1 },
    ingredients: [
      { item: `diamond_${slot}`, count: 1 },
      { item: `gold_${slot}`, count: 1 },
      { item: `redstone_${slot}`, count: 1 },
    ],
    category: 'Güç Zırhı',
    desc: 'Elmas + Altın + Redstone parçasını birleştirir.',
  });
}

// ------------------------------------------------------------ istasyonlar
recipes.register(
  {
    id: 'place_workbench',
    station: 'hand',
    output: { item: 'place_workbench', count: 1 },
    ingredients: [{ item: 'wood', count: 20 }],
    category: 'Yapı',
  },
  {
    id: 'place_furnace',
    station: 'hand',
    output: { item: 'place_furnace', count: 1 },
    ingredients: [{ item: 'stone', count: 16 }],
    category: 'Yapı',
  },
  {
    id: 'place_grinder',
    station: 'workbench',
    output: { item: 'place_grinder', count: 1 },
    ingredients: [
      { item: 'stone', count: 12 },
      { item: 'iron', count: 4 },
    ],
    category: 'Yapı',
  },
  {
    id: 'place_chest',
    station: 'hand',
    output: { item: 'place_chest', count: 1 },
    ingredients: [{ item: 'wood', count: 12 }],
    category: 'Yapı',
  },
);

// ------------------------------------------------------------ yapı parçaları (maliyetlerinden türetilir)
for (const piece of buildPieces.all()) {
  if (piece.shape === 'station' || piece.shape === 'crop') continue;
  recipes.register({
    id: `place_${piece.id}`,
    station: piece.tier > 0 ? 'workbench' : 'hand',
    output: { item: `place_${piece.id}`, count: 1 },
    ingredients: piece.cost,
    category: 'Yapı',
  });
}

/** Desenin dolu hücrelerini normalize eder (kenar boşluklarını kırpar). */
export function normalizePattern(pattern: string[]): { w: number; h: number; cells: (string | null)[][] } {
  const rows = pattern.map((r) => r.split(''));
  let minC = Infinity;
  let maxC = -Infinity;
  let minR = Infinity;
  let maxR = -Infinity;
  rows.forEach((row, r) => {
    row.forEach((ch, c) => {
      if (ch !== ' ') {
        if (c < minC) minC = c;
        if (c > maxC) maxC = c;
        if (r < minR) minR = r;
        if (r > maxR) maxR = r;
      }
    });
  });
  if (minC === Infinity) return { w: 0, h: 0, cells: [] };
  const cells: (string | null)[][] = [];
  for (let r = minR; r <= maxR; r++) {
    const row: (string | null)[] = [];
    for (let c = minC; c <= maxC; c++) {
      const ch = rows[r]?.[c] ?? ' ';
      row.push(ch === ' ' ? null : ch);
    }
    cells.push(row);
  }
  return { w: maxC - minC + 1, h: maxR - minR + 1, cells };
}
